import {
  AbstractComparatorApiService,
  AddressApiSuccessResponse,
  AddressData,
  CalculateParameters,
  Comparison,
  CustormerBenefitItem,
  FeaturesEdge,
  IComparatorApiServiceParameters,
  IGraphProvider,
  OrderResult,
} from '../../..';
import { MasterAgreementStructure, OrderStatusResult } from '../../../home';
import { IProduct, QuoteResponse } from '../../../product';
import { ApolloProvider } from '../apolloProvider';
import { LegacyOrderResponse } from '../../../../../helpers';
import { gql } from '@apollo/client';
import { OSBusinessCarQueryProvider } from './osBusinessCarQueryProvider';
import { validateAddressCommon } from '../commonGraphQLAPI';
import { KiziLogger } from '../../../../../components/Logger';
import { calculationInputQuery } from '../../queries/os-business-car/calculationInputQuery';
import {
  productsWithSuppliersQuery,
  sendEmailToCustomerQueryBusinessCar,
  sendInactivityEmailToCustomerQueryBusinessCar,
} from '../../queries';
import { HomeProduct, ProductsWithSuppliers } from '../../../graphProduct';
import {
  BusinessCar_CommonTechnical,
  BusinessCar_PolicyDetails,
  TriggerEventPayload,
} from './types';
import { calculateAdditionalCoveragesQuery } from '../../queries/os-business-car/calculateAdditionalCoveragesQuery';

export class GraphOSBusinessCarAPI extends AbstractComparatorApiService {
  private client;
  private queryProvider: OSBusinessCarQueryProvider;
  private readonly graphProvider: IGraphProvider;

  constructor(params: IComparatorApiServiceParameters) {
    super(params);
    this.queryProvider = new OSBusinessCarQueryProvider();
    this.graphProvider = new ApolloProvider(params);
  }

  setClient(client) {
    this.client = client;
  }

  getClient() {
    return this.client;
  }

  getComparison(): Promise<Comparison> {
    throw new Error('Method not implemented.');
  }

  validateAddress(
    postcode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressApiSuccessResponse> {
    const variables = this.queryProvider.validateAddressVariables(
      postcode,
      parseInt(houseNumber),
      houseNumberAddition
    );

    return validateAddressCommon(this.graphProvider, variables);
  }

  getProducts(
    searchCriteria: any,
    selectedFilters: object,
    requestOptions: any,
    quotePrefillProps: any
  ): Promise<IProduct[]> {
    console.log({
      searchCriteria,
      selectedFilters,
      requestOptions,
      quotePrefillProps,
    });
    return new Promise<IProduct[]>((resolve, reject) => {
      const variables = this.queryProvider.calculateBusinessCarVariables(
        searchCriteria,
        selectedFilters,
        this?.credentials?.clientId
      );

      let packages: BusinessCar_PolicyDetails[] = [];
      let products: HomeProduct[] = [];
      let suppliers: any[] = [];
      let commonTechnical: BusinessCar_CommonTechnical[];

      this.graphProvider
        .mutate<any>(
          gql(calculationInputQuery),
          variables,
          'calculate',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/businesscar/graphql`
        )
        .then((data) => {
          if (data.calculationResult && data.calculationResult.policyDetails) {
            packages = data.calculationResult.policyDetails;
            const result = data.calculationResult;

            if (Array.isArray(result.commonTechnical)) {
              commonTechnical =
                result.commonTechnical as BusinessCar_CommonTechnical[];
            }

            const productsWithSuppliersQueryVars =
              this.queryProvider.validateSuppliersVariables(result);

            this.graphProvider
              .query<ProductsWithSuppliers>(
                gql(productsWithSuppliersQuery),
                productsWithSuppliersQueryVars,
                'productsWithSuppliers',
                'network-only',
                `${process.env.KIZI_PUBLIC_DOMAIN}/product/graphql`
              )
              .then(async (productsWithSuppliers) => {
                products = productsWithSuppliers?.products;
                suppliers = productsWithSuppliers?.suppliers;

                const formatted = this.queryProvider.formatBusinessCarProduct(
                  this.credentials,
                  packages,
                  products,
                  suppliers,
                  commonTechnical,
                  selectedFilters,
                  searchCriteria
                );
                console.log({ formatted });
                resolve(formatted);
              })
              .catch((error) => {
                KiziLogger.logError({
                  eventId: 0,
                  member: 'business-car - getProducts',
                  message: 'Error querying products with suppliers',
                  eventName: 'productsWithSuppliersError',
                  data: {
                    error,
                    variables: productsWithSuppliersQueryVars,
                  },
                });
                reject(error);
              });
          } else {
            reject('No products returned');
          }
        })
        .catch((error) => {
          KiziLogger.logError({
            eventId: 0,
            member: 'business-car - getProducts',
            message: 'Error calculating products',
            eventName: 'calculateError',
            data: {
              error,
              variables,
            },
          });
          reject('No products returned');
        });
    });
  }

  calculateAdditionalCoverages(
    searchCriteria: any,
    selectedFilters: object,
    refKey: string
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const variables = this.queryProvider.calculateBusinessCarVariables(
        searchCriteria,
        selectedFilters,
        this?.credentials?.clientId,
        refKey
      );

      this.graphProvider
        .mutate<any>(
          gql(calculateAdditionalCoveragesQuery),
          variables,
          'calculateAdditionalCoverages',
          'network-only',
          `${process.env.KIZI_PUBLIC_DOMAIN}/businesscar/graphql`
        )
        .then((result) => {
          console.log({ result });
          if (result && result.calculateAdditionalCoveragesResult) {
            const formattedResult =
              this.queryProvider.formatAdditionalCoveragesResult(result);
            resolve(formattedResult);
          } else {
            KiziLogger.logError({
              eventId: 0,
              member: 'business-car - calculateAdditionalCoverages',
              message: 'No additional coverages returned',
              eventName: 'additionalCoveragesError',
              data: {
                variables,
                result,
              },
            });
            reject('No additional coverages returned');
          }
        })
        .catch((error) => {
          KiziLogger.logError({
            eventId: 0,
            member: 'business-car - calculateAdditionalCoverages',
            message: 'Error calculating additional coverages',
            eventName: 'calculateAdditionalCoveragesError',
            data: {
              error,
              variables,
            },
          });
          reject(error);
        });
    });
  }

  getCustomerBenefits(): Promise<CustormerBenefitItem[]> {
    throw new Error('Method not implemented.');
  }

  getQuotePrefill(): Promise<QuoteResponse | false> {
    throw new Error('Method not implemented');
  }

  getAddressData(): Promise<AddressData> {
    throw new Error('Method not implemented.');
  }

  getHomeFeaturesDetails(): any {
    throw new Error('Method not implemented.');
  }

  handleFeatures(): Promise<FeaturesEdge[]> {
    throw new Error('Method not implemented.');
  }

  orderAffiliateContentPackage(): Promise<OrderResult> {
    throw new Error('Method not implemented.');
  }

  getCalculationInput(): Promise<MasterAgreementStructure> {
    throw new Error('Method not implemented.');
  }

  orderStatus(): Promise<OrderStatusResult> {
    throw new Error('Method not implemented.');
  }

  getOrderDefinition(): Promise<LegacyOrderResponse['data']> {
    throw new Error('Method not implemented.');
  }

  // private getCalculateSessionId(): string {
  //   return Math.random().toString(36).substring(7);
  // }

  sendEmailToCustomer(
    comparisonId: string | number,
    email: string,
    productType: string,
    inactivity: boolean
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const variables = this.queryProvider.validateEmailVariables(
        comparisonId,
        email,
        productType
      );

      const query = inactivity
        ? sendInactivityEmailToCustomerQueryBusinessCar
        : sendEmailToCustomerQueryBusinessCar;

      const newClientLink = `${process.env.KIZI_PUBLIC_DOMAIN}/events/graphql`;

      this.graphProvider
        .mutate<TriggerEventPayload>(
          gql(query),
          variables,
          'triggerEvent',
          undefined,
          newClientLink
        )
        .then((data: TriggerEventPayload) => {
          if (data.success) {
            resolve('Email sent successfully');
          } else {
            reject('Email could not be sent');
          }
        })
        .catch((err) => {
          console.error(err);
          reject('Email could not be sent');
        });
    });
  }

  calculateHomeContentPackage(
    calculateParameters: CalculateParameters
  ): Promise<MasterAgreementStructure> {
    console.log('calculateHomeContentPackage', calculateParameters);
    throw new Error('calculateHomeContentPackage method not implemented.');
  }

  checkMembershipStatus(): Promise<CustormerBenefitItem[]> {
    throw new Error('Method not implemented.');
  }
}
